
import { Component, Input, computed, signal, effect } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { PlayoffDetail } from '../../models/playoff-detail';
import { Tournament } from '../../models';
import { PlayoffStateService, PlayoffHoleInput, PlayoffCourseOption } from '../../services/playoff/playoff-state.service';
import { Hole } from '../../models/hole';

@Component({
    selector: 'app-playoff-management',
    standalone: true,
    imports: [ReactiveFormsModule],
    templateUrl: './playoff-management.component.html',
    styleUrls: ['./playoff-management.component.scss']
})
export class PlayoffManagementComponent {
    @Input() detail: PlayoffDetail | null = null;
    @Input() tournament: Tournament | null = null;

    readonly courseOptions = computed(() => this.playoffState.courseOptions());

    readonly courseForm = this.fb.group({
        courseId: ['', Validators.required],
        holeNumber: ['', Validators.required],
        par: [4, [Validators.required, Validators.min(1), Validators.max(10)]],
        yardage: [150, [Validators.required, Validators.min(1)]]
    });

    readonly isSavingHole = signal(false);
    readonly isRemovingHole = signal(false);
    readonly currentCourseHoles = signal<Hole[]>([]);

    constructor(
        private readonly fb: FormBuilder,
        private readonly playoffState: PlayoffStateService
    ) {
        // Auto-select the course when there's only one available
        effect(() => {
            const options = this.courseOptions();
            const currentCourseId = this.courseForm.value.courseId;

            if (options.length === 1 && !currentCourseId) {
                const singleCourse = options[0];
                this.courseForm.patchValue({ courseId: singleCourse.courseId });
                this.onCourseChange(singleCourse.courseId);
            }
        });
    }

    onCourseChange(courseId: string): void {
        this.currentCourseHoles.set(this.playoffState.getCourseHoles(courseId));
        this.courseForm.patchValue({ holeNumber: '' });
    }

    onHoleChange(rawHoleNumber: string): void {
        const holeNumber = Number(rawHoleNumber);
        if (Number.isNaN(holeNumber)) {
            return;
        }

        const hole = this.currentCourseHoles().find((candidate) => candidate.number === holeNumber);
        if (hole?.par) {
            this.courseForm.patchValue({ par: hole.par });
        }
    }

    async addHole(): Promise<void> {
        if (this.courseForm.invalid || !this.detail) {
            return;
        }

        const { courseId, holeNumber, par, yardage } = this.courseForm.value;
        const payload: PlayoffHoleInput = {
            courseId: courseId!,
            holeNumber: Number(holeNumber),
            par: Number(par),
            yardage: Number(yardage)
        };

        if (!payload.courseId || Number.isNaN(payload.holeNumber) || Number.isNaN(payload.par) || Number.isNaN(payload.yardage)) {
            return;
        }

        this.isSavingHole.set(true);
        const success = await this.playoffState.addHoleToPlayoff(payload);
        this.isSavingHole.set(false);

        if (success) {
            this.courseForm.patchValue({ holeNumber: '', par: payload.par, yardage: payload.yardage });
        }
    }

    canRemoveLastHole(): boolean {
        return this.detail !== null && this.detail.holes.length > 0;
    }

    async removeLastHole(): Promise<void> {
        if (!this.detail || this.detail.holes.length === 0) {
            return;
        }

        const lastHole = this.detail.holes[this.detail.holes.length - 1];
        this.isRemovingHole.set(true);
        const success = await this.playoffState.removeLastPlayoffHole(lastHole);
        this.isRemovingHole.set(false);
    }

    readonly trackCourse = (_: number, course: PlayoffCourseOption): string => course.courseId;

    readonly trackHole = (_: number, hole: Hole): string => `${hole.courseId ?? 'course'}-${hole.number}`;
}
